import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardSidebar from '../components/DashboardSidebar';
import { Button } from '../components/ui/button';
import { 
  FileText, Clock, CheckCircle, AlertCircle, 
  ArrowRight, Plus, Globe, Calendar, Pencil, Trash2
} from 'lucide-react';
import { applicationsApi } from '../lib/api';
import CountryFlag from '../components/CountryFlag';
import { toast } from 'sonner';

const ApplicantDashboard = () => {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchApplications = async () => {
    try {
      const response = await applicationsApi.getMyApplications();
      setApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const handleDelete = async (e, applicationId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    try {
      await applicationsApi.delete(applicationId);
      toast.success('Application deleted');
      fetchApplications();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const stats = {
    total: applications.length,
    pending: applications.filter(a => ['draft', 'submitted', 'under_review', 'processing'].includes(a.status)).length,
    approved: applications.filter(a => a.status === 'approved').length,
    rejected: applications.filter(a => a.status === 'rejected').length
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'rejected': return <AlertCircle className="w-5 h-5 text-red-600" />;
      case 'submitted':
      case 'under_review':
      case 'processing':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      default: return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      case 'submitted':
      case 'under_review':
      case 'processing':
        return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F0E6]">
      <DashboardSidebar />
      
      <main className="dashboard-content p-6 lg:p-8 pt-20 lg:pt-8" data-testid="applicant-dashboard">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl lg:text-3xl font-bold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-[#4A5568] mt-1">Manage your visa applications and track their status.</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-6 border border-[#D1CBBF]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#4A5568]">Total Applications</p>
                <p className="text-2xl font-bold text-[#0A1E40]">{stats.total}</p>
              </div>
              <div className="w-12 h-12 bg-[#0A1E40]/10 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-[#0A1E40]" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 border border-[#D1CBBF]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#4A5568]">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 border border-[#D1CBBF]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#4A5568]">Approved</p>
                <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 border border-[#D1CBBF]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#4A5568]">Rejected</p>
                <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-[#0A1E40] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/apply" className="block" data-testid="new-application-card">
              <div className="bg-[#FF5A36] text-white rounded-xl p-6 card-hover">
                <Plus className="w-8 h-8 mb-3" />
                <h3 className="font-semibold text-lg mb-1">New Application</h3>
                <p className="text-white/80 text-sm">Start a new visa application</p>
              </div>
            </Link>
            <Link to="/countries" className="block">
              <div className="bg-white border border-[#D1CBBF] rounded-xl p-6 card-hover">
                <Globe className="w-8 h-8 text-[#0A1E40] mb-3" />
                <h3 className="font-semibold text-lg text-[#0A1E40] mb-1">Browse Destinations</h3>
                <p className="text-[#4A5568] text-sm">Explore visa options by country</p>
              </div>
            </Link>
            <Link to="/my-applications" className="block">
              <div className="bg-white border border-[#D1CBBF] rounded-xl p-6 card-hover">
                <Calendar className="w-8 h-8 text-[#0A1E40] mb-3" />
                <h3 className="font-semibold text-lg text-[#0A1E40] mb-1">Track Applications</h3>
                <p className="text-[#4A5568] text-sm">View all your applications</p>
              </div>
            </Link>
          </div>
        </div>

        {/* Recent Applications */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Recent Applications
            </h2>
            <Link to="/my-applications">
              <Button variant="ghost" className="text-[#FF5A36]">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="bg-white rounded-xl p-8 text-center border border-[#D1CBBF]">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/3 mx-auto mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
              </div>
            </div>
          ) : applications.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center border border-[#D1CBBF]">
              <div className="w-16 h-16 rounded-2xl bg-[#F5F0E6] flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-[#D1CBBF]" />
              </div>
              <h3 className="font-bold text-[#0A1E40] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No Applications Yet</h3>
              <p className="text-[#4A5568] text-sm mb-5">Start your visa journey today!</p>
              <Link to="/apply">
                <Button className="bg-[#FF5A36] hover:bg-[#E0492B] rounded-xl px-6">
                  <Plus className="w-4 h-4 mr-2" /> Create Application
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {applications.slice(0, 5).map((app) => {
                const traveler = app.travelers?.[0];
                const clientName = `${traveler?.first_name || ''} ${traveler?.last_name || ''}`.trim() || user?.name || 'Applicant';
                const isPaid = app.payment_status === 'completed';
                return (
                <div key={app.application_id} className="bg-white rounded-2xl border border-[#D1CBBF] overflow-hidden hover:shadow-md transition-all" data-testid={`application-card-${app.application_id}`}>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-11 h-11 rounded-xl bg-[#0A1E40] flex items-center justify-center shrink-0">
                          <span className="text-white font-bold text-sm">{clientName.charAt(0).toUpperCase()}</span>
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-bold text-[#0A1E40] truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{clientName}</h3>
                          <div className="flex items-center gap-2 text-xs text-[#4A5568]">
                            <CountryFlag code={app.country?.code} size="xs" />
                            <span>{app.country?.name}</span>
                            <span className="text-[#D1CBBF]">·</span>
                            <span>{app.visa_type?.name}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold capitalize ${getStatusColor(app.status)}`}>
                          {app.status?.replace('_', ' ')}
                        </span>
                        {!isPaid && (
                          <>
                            <Link to={`/apply?edit=${app.application_id}`} onClick={(e) => e.stopPropagation()}>
                              <Button size="sm" variant="outline" className="h-8 px-2 border-[#D1CBBF] text-[#4A5568] hover:text-[#FF5A36] hover:border-[#FF5A36]">
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                            </Link>
                            <Button size="sm" variant="outline" className="h-8 px-2 border-[#D1CBBF] text-[#4A5568] hover:text-red-500 hover:border-red-300" onClick={(e) => handleDelete(e, app.application_id)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-[#F5F0E6] rounded-xl p-3">
                      <div>
                        <p className="text-[10px] text-[#4A5568] uppercase tracking-wider">App ID</p>
                        <Link to={`/applications/${app.application_id}`} className="text-xs text-[#FF5A36] font-bold hover:underline">{app.application_id}</Link>
                      </div>
                      <div>
                        <p className="text-[10px] text-[#4A5568] uppercase tracking-wider">Travelers</p>
                        <p className="text-xs font-bold text-[#0A1E40]">{app.num_travelers}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-[#4A5568] uppercase tracking-wider">Fee</p>
                        <p className="text-xs font-bold text-[#0A1E40]">₹{app.total_fee?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-[#4A5568] uppercase tracking-wider">Payment</p>
                        <p className={`text-xs font-bold ${isPaid ? 'text-green-600' : 'text-[#FF5A36]'}`}>{isPaid ? 'Paid' : 'Pending'}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#F5F0E6]">
                      <div className="flex items-center gap-4 text-xs text-[#4A5568]">
                        <span>Travel: {new Date(app.travel_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                        <span className="text-[#D1CBBF]">·</span>
                        <span>Created: {new Date(app.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      </div>
                      <Link to={`/applications/${app.application_id}`} className="text-xs text-[#FF5A36] font-semibold hover:underline flex items-center gap-1">
                        View Details <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ApplicantDashboard;
